import { useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, Check, Pencil, RotateCcw, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface KpiMetric {
  label: string;
  value: string | number;
  unit?: string;
  tone?: 'default' | 'success' | 'warning' | 'destructive';
}

interface Props {
  title: string;
  /** Если title переопределён пользователем — показываем "Оригинал" под input'ом и даём reset. */
  defaultTitle?: string;
  isCustomTitle?: boolean;
  /** Колбэк смены имени. value=null означает сброс на дефолт. Не передан → редактирование отключено. */
  onTitleChange?: (value: string | null) => void;
  isSavingTitle?: boolean;

  status: 'active' | 'paused';
  metrics: KpiMetric[];
  errorMessage?: string | null;
  href?: string;
  linkLabel?: string;
}

const toneClasses: Record<NonNullable<KpiMetric['tone']>, string> = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

export function PipelineKpiCard({
  title,
  defaultTitle,
  isCustomTitle,
  onTitleChange,
  isSavingTitle,
  status,
  metrics,
  errorMessage,
  href,
  linkLabel,
}: Props): JSX.Element {
  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
        <EditableTitle
          title={title}
          defaultTitle={defaultTitle}
          isCustomTitle={isCustomTitle}
          onTitleChange={onTitleChange}
          isSaving={isSavingTitle}
        />
        <Badge variant={status === 'active' ? 'success' : 'secondary'} className="shrink-0">
          {status === 'active' ? 'Active' : 'Paused'}
        </Badge>
      </CardHeader>

      <CardContent className="grid grid-cols-2 gap-3">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-md border border-border/60 bg-muted/30 px-3 py-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {m.label}
            </div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className={cn('text-lg font-bold', toneClasses[m.tone ?? 'default'])}>
                {m.value}
              </span>
              {m.unit && <span className="text-xs text-muted-foreground">{m.unit}</span>}
            </div>
          </div>
        ))}
      </CardContent>

      {(errorMessage || href) && (
        <CardFooter className="flex flex-col gap-3 border-t border-border pt-4">
          {errorMessage && (
            <div className="flex w-full items-start gap-2 rounded-md border border-destructive/20 bg-destructive/10 p-3">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
              <p className="line-clamp-2 flex-1 text-xs text-destructive">{errorMessage}</p>
            </div>
          )}
          {href && (
            <Button variant="ghost" className="group w-full justify-between" asChild>
              <Link to={href}>
                <span>{linkLabel ?? 'Открыть pipeline'}</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  );
}

interface EditableTitleProps {
  title: string;
  defaultTitle?: string;
  isCustomTitle?: boolean;
  onTitleChange?: (value: string | null) => void;
  isSaving?: boolean;
}

function EditableTitle({
  title,
  defaultTitle,
  isCustomTitle,
  onTitleChange,
  isSaving,
}: EditableTitleProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Если title пришёл другой (refetch) и мы не редактируем — синхронизируем draft.
  useEffect(() => {
    if (!isEditing) setDraft(title);
  }, [title, isEditing]);

  const canEdit = typeof onTitleChange === 'function';
  const showResetHint = isCustomTitle && defaultTitle && defaultTitle !== title;

  const commit = (): void => {
    const trimmed = draft.trim();
    if (trimmed === title) {
      setIsEditing(false);
      return;
    }
    onTitleChange?.(trimmed.length === 0 ? null : trimmed);
    setIsEditing(false);
  };

  const cancel = (): void => {
    setDraft(title);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-1 pr-2">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
          onBlur={commit}
          disabled={isSaving}
          maxLength={120}
          className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1 text-base font-semibold outline-none focus:ring-2 focus:ring-ring"
        />
        <button
          type="button"
          aria-label="Сохранить"
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
          disabled={isSaving}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Отмена"
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancel}
          disabled={isSaving}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="group/title flex min-w-0 flex-1 flex-col gap-0.5 pr-2">
      <div className="flex min-w-0 items-center gap-1.5">
        <CardTitle className="truncate text-base font-semibold" title={title}>
          {title}
        </CardTitle>
        {canEdit && (
          <button
            type="button"
            aria-label="Переименовать"
            onClick={() => setIsEditing(true)}
            className="shrink-0 rounded-md p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/title:opacity-100"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {showResetHint && canEdit && (
        <button
          type="button"
          onClick={() => onTitleChange?.(null)}
          disabled={isSaving}
          className="flex items-center gap-1 self-start text-[11px] text-muted-foreground/70 hover:text-foreground"
          title={`Сбросить на «${defaultTitle}»`}
        >
          <RotateCcw className="h-3 w-3" />
          <span className="truncate max-w-[200px]">{defaultTitle}</span>
        </button>
      )}
    </div>
  );
}
